import { useCallback, useEffect, useState } from "react";

import { functionalUpdate } from "@tanstack/react-table";

import type { ColumnVisibilityState, Updater } from "@tanstack/react-table";

import type { StoredColumnVisibility } from "./useStoredColumnVisibility.types";

const STORAGE_PREFIX = "dataTable:hiddenColumns:";

function readHiddenColumns(storageKey: string): string[] {
  try {
    const stored: unknown = JSON.parse(
      localStorage.getItem(STORAGE_PREFIX + storageKey) ?? "[]",
    );
    return Array.isArray(stored)
      ? stored.filter((id): id is string => typeof id === "string")
      : [];
  } catch {
    return [];
  }
}

function writeHiddenColumns(storageKey: string, hiddenIds: string[]): void {
  try {
    localStorage.setItem(
      STORAGE_PREFIX + storageKey,
      JSON.stringify(hiddenIds),
    );
  } catch {
    // Stockage indisponible (navigation privée…) : le choix vaut pour la session.
  }
}

function readColumnVisibility(
  storageKey: string | undefined,
): ColumnVisibilityState {
  return Object.fromEntries(
    (storageKey ? readHiddenColumns(storageKey) : []).map((id) => [id, false]),
  );
}

export default function useStoredColumnVisibility(
  storageKey: string | undefined,
): StoredColumnVisibility {
  const [columnVisibility, setColumnVisibility] =
    useState<ColumnVisibilityState>(() => readColumnVisibility(storageKey));
  const [previousStorageKey, setPreviousStorageKey] = useState(storageKey);

  // Autre clé sur le même composant : on relit son choix pendant le rendu, avant que l'effet ne l'écrase.
  if (storageKey !== previousStorageKey) {
    setPreviousStorageKey(storageKey);
    setColumnVisibility(readColumnVisibility(storageKey));
  }

  useEffect(() => {
    if (!storageKey) return;

    writeHiddenColumns(
      storageKey,
      Object.keys(columnVisibility).filter(
        (id) => columnVisibility[id] === false,
      ),
    );
  }, [storageKey, columnVisibility]);

  const onColumnVisibilityChange = useCallback(
    (updater: Updater<ColumnVisibilityState>) =>
      setColumnVisibility((previous) => functionalUpdate(updater, previous)),
    [],
  );

  return { columnVisibility, onColumnVisibilityChange };
}
